import type { FC, ReactNode } from 'react';
import { capitalCase } from 'change-case';
import {
    TbCode,
    TbFileText,
    TbInfoCircle,
    TbLink,
    TbList,
    TbListCheck,
    TbListNumbers,
    TbPhoto,
    TbQuote,
    TbTextSize,
} from 'react-icons/tb';

import { ContextualCommentComposer, ReportComponentIcon } from '@/shared';
import { ReportComponentType } from '@/types';
import type { CommentComposerValue, ResourceActivityResourceType } from '@/types';

import { MemoCommentTarget } from './memoCommentUtils';

type CommentTargetMetadata = Record<string, unknown>;

interface MemoCommentPresentation {
    avatar?: ReactNode;
    description: string;
    icon?: ReactNode;
}

interface MemoCommentComposerProps {
    disabled?: boolean;
    onSubmit: (value: CommentComposerValue) => Promise<boolean | void> | boolean | void;
    resourceId: string;
    resourceType: ResourceActivityResourceType;
    submitting?: boolean;
    target?: MemoCommentTarget;
}

const reportComponentTypes = new Set<string>(Object.values(ReportComponentType));

const isMetadataRecord = (value: unknown): value is CommentTargetMetadata =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const readMetadataLabel = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const getMemoCommentPresentation = (target?: MemoCommentTarget): MemoCommentPresentation => {
    const targetMetadata = isMetadataRecord(target?.metadata.target) ? target.metadata.target : undefined;
    const nestedNode = isMetadataRecord(targetMetadata?.node) ? targetMetadata.node : undefined;
    const componentType =
        readMetadataLabel(targetMetadata?.componentType) ?? readMetadataLabel(nestedNode?.componentType);
    const nodeType = readMetadataLabel(targetMetadata?.nodeType) ?? readMetadataLabel(nestedNode?.nodeType);
    const targetType = readMetadataLabel(targetMetadata?.type);

    if (componentType && reportComponentTypes.has(componentType)) {
        return {
            avatar: <ReportComponentIcon size={28} type={componentType as ReportComponentType} />,
            description: componentType,
        };
    }

    const iconByNodeType: Record<string, ReactNode> = {
        blockquote: <TbQuote />,
        bulletList: <TbList />,
        callout: <TbInfoCircle />,
        codeBlock: <TbCode />,
        iframe: <TbLink />,
        image: <TbPhoto />,
        linkEmbed: <TbLink />,
        orderedList: <TbListNumbers />,
        paragraph: <TbTextSize />,
        taskList: <TbListCheck />,
        unfurledLink: <TbLink />,
        youtube: <TbPhoto />,
    };
    const contextLabel = capitalCase(nodeType ?? targetType ?? 'memo context');
    const description = targetType === 'text-selection' ? `Text Selection · ${contextLabel}` : contextLabel;

    return {
        description,
        icon: (nodeType && iconByNodeType[nodeType]) || <TbFileText />,
    };
};

const MemoCommentComposer: FC<MemoCommentComposerProps> = ({
    disabled,
    onSubmit,
    resourceId,
    resourceType,
    submitting,
    target,
}): JSX.Element => {
    const presentation = getMemoCommentPresentation(target);

    return (
        <ContextualCommentComposer
            avatar={presentation.avatar}
            disabled={disabled}
            headerLabel={target?.label ?? 'Selection'}
            icon={presentation.icon}
            metadataDescription={presentation.description}
            onSubmit={onSubmit}
            resourceId={resourceId}
            resourceType={resourceType}
            submitting={submitting}
        />
    );
};

export default MemoCommentComposer;
